import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, User, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { StructuredResponse } from './StructuredResponse';
import type { ECOMessage } from './types';

interface ChatBubbleProps {
  message: ECOMessage;
  isStreaming?: boolean;
  streamingContent?: string;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({ message, isStreaming = false, streamingContent }) => {
  // Debug logging
  React.useEffect(() => {
    if (isStreaming) {
      console.log('ChatBubble - streaming update:', { 
        messageId: message.id, 
        isStreaming, 
        streamingContentLength: streamingContent?.length || 0,
        streamingContentPreview: streamingContent?.slice(0, 50) + '...'
      });
    }
  }, [isStreaming, streamingContent, message.id]);
  
  // During streaming: show streaming content, otherwise show final message content
  const displayContent = isStreaming ? (streamingContent || '') : message.content;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      toast.success('Content copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy content');
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isStructured = message.templateUsed === 'Entire ECO' || displayContent.includes('💡') || displayContent.includes('📌');

  const renderContent = () => {
    if (message.type === 'user') {
      return (
        <div className="whitespace-pre-wrap text-sm leading-relaxed">
          {displayContent}
        </div>
      );
    } else {
      return (
        <div className="relative">
          <StructuredResponse 
            content={displayContent} 
            isStructured={isStructured}
          />
          {isStreaming && (
            <span className="inline-block w-2 h-5 bg-current animate-pulse ml-1 align-text-bottom">|</span>
          )}
        </div>
      );
    }
  };

  return (
    <div className={`group flex gap-3 py-4 px-6 hover:bg-muted/30 transition-colors ${
      message.type === 'user' ? 'bg-muted/20' : ''
    }`}>
      {/* Avatar */}
      <div className={`
        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
        ${message.type === 'user' 
          ? 'bg-gradient-to-br from-purple-600 to-purple-400 text-white' 
          : 'bg-card border-2 border-border text-muted-foreground'
        }
      `}>
        {message.type === 'user' ? (
          <User className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {message.type === 'user' ? 'You' : 'ECO Assistant'}
          </span>
          {message.templateUsed && (
            <Badge variant="secondary" className="text-xs">
              {message.templateUsed}
            </Badge>
          )}
        </div>

        <div className="prose prose-sm max-w-none dark:prose-invert">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{formatTime(message.timestamp)}</span>
            {message.tokens && (
              <>
                <span>•</span>
                <span>{message.tokens} tokens</span>
              </>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};